import { StrictMode, type ReactNode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { BrowserRouter, MemoryRouter, Route, Routes } from 'react-router-dom';
import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import BoardPage from './app/components/board/BoardPage';
import TeamsSetupPage from './app/teams/TeamsSetupPage';
import { appTheme } from './app/theme';
import './styles.scss';

export type DotvotingRouterType = 'browser' | 'memory';

export interface DotvotingMountOptions {
  basename?: string;
  initialPath?: string;
  router?: DotvotingRouterType;
  strictMode?: boolean;
}

export interface DotvotingMountHandle {
  unmount: () => void;
}

const mountedRoots = new WeakMap<HTMLElement, ReactDOM.Root>();

const DotvotingRoutes = () => (
  <ThemeProvider theme={appTheme}>
    <CssBaseline />
    <Routes>
      <Route path="/b/:boardId" element={<BoardPage />} />
      <Route path="/teams-setup" element={<TeamsSetupPage />} />
    </Routes>
  </ThemeProvider>
);

const withRouter = (options: DotvotingMountOptions, children: ReactNode) => {
  const router = options.router ?? 'browser';
  const basename = options.basename;

  if (router === 'memory') {
    return (
      <MemoryRouter basename={basename} initialEntries={[options.initialPath ?? '/']}>
        {children}
      </MemoryRouter>
    );
  }

  return <BrowserRouter basename={basename}>{children}</BrowserRouter>;
};

const resolveContainer = (target: string | HTMLElement) => {
  if (typeof target === 'string') {
    const node = document.querySelector<HTMLElement>(target);
    if (!node) {
      throw new Error(`Could not find dotvoting mount target: ${target}`);
    }

    return node;
  }

  return target;
};

export const mountDotvoting = (
  target: string | HTMLElement,
  options: DotvotingMountOptions = {}
): DotvotingMountHandle => {
  const container = resolveContainer(target);
  const existingRoot = mountedRoots.get(container);

  if (existingRoot) {
    existingRoot.unmount();
  }

  const root = ReactDOM.createRoot(container);
  mountedRoots.set(container, root);

  const app = withRouter(options, <DotvotingRoutes />);
  const shouldUseStrictMode = options.strictMode ?? false;

  root.render(shouldUseStrictMode ? <StrictMode>{app}</StrictMode> : app);

  return {
    unmount: () => {
      const currentRoot = mountedRoots.get(container);
      if (!currentRoot) {
        return;
      }

      currentRoot.unmount();
      mountedRoots.delete(container);
    },
  };
};

declare global {
  interface Window {
    DotstormingDotvoting?: {
      mountDotvoting: typeof mountDotvoting;
    };
  }
}

window.DotstormingDotvoting = {
  mountDotvoting,
};
